import {
  Injectable
} from '@angular/core';

import {
  HttpClient
} from '@angular/common/http';

import {
  Router,
  UrlSerializer
} from '@angular/router';

import {
  map
} from 'rxjs/operators';

import {
  DiscountWms,
  PricelistWms,
  RatiosWms
} from '../models';

import {
  getAuthHeaders,
  getDomainUrlPath,
  getCountryName,
  getCurrencySymbol
} from '../app/app.utils';

import {
  _WMS_URL,

  COST_LIST_MARKUP_DEFAULT_MAP,
  PRICELIST_DISCOUNT_DEFAULT_MAP,
  PRICELIST_MARKUP_DEFAULT_MAP,

  PRICELIST_WMS_DISCOUNT_DEFAULTS
} from '../app/app.const';

@Injectable()
export class PricelistWmsService {
  public authToken: string;

  public cloneDiscountRatioMap: RatiosWms[] = [];

  public discountDefaults: DiscountWms[] = [
    ...PRICELIST_WMS_DISCOUNT_DEFAULTS
  ];

  public markupRatioMap: RatiosWms[] = [];

  public markupCostsRatioMap: RatiosWms[] = [];
  
  constructor(
    private http: HttpClient,
    private router: Router,
    private urlSerializer: UrlSerializer
  ) {}

  public get(
    _id: string,
    domain?: string
  ): Promise<PricelistWms> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/${_id}`,
              getAuthHeaders(
                this.authToken
              )
            )
            .pipe(
              map(
                (
                  pricelist: PricelistWms
                ) => ({
                  ...pricelist,
                  currencySymbol: (
                    getCurrencySymbol(
                      pricelist
                        .currencyCode
                    )
                  ),
                })
              )
            )
            .toPromise()
      )
    );
  }
  
  public getAll(
    context?: string,
    domain?: string,
    term?: string,
    partnerCode?: string,
    countryCode: string = undefined,
    userId?: string,
    sort?: string,
    order: string = 'asc',
    limit?: number,
    skip?: number
  ): Promise<PricelistWms[]> {
    const query = (
      this.urlSerializer
          .serialize(
            this.router
                .createUrlTree(
                  [
                    `${(
                      (
                        context ===
                        'live'
                      ) ?
                        '' :
                        context
                    )}`
                  ],
                  {
                    queryParams: {
                      term,
                      partnerCode,
                      userId,
                      sort,
                      order,
                      limit,
                      skip
                    }
                  }
                )
          )
    );

    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/${(
                  getDomainUrlPath(
                    domain
                  )
                )}${query}`,
              getAuthHeaders(
                this.authToken
              )
            )
            .pipe(
              map(
                (
                  data: {
                    count: number,
                    pricelists: PricelistWms[]
                  }
                ) => (
                  data
                    .pricelists
                    .map(
                      pricelist => ({
                        ...pricelist,
                        currencySymbol: (
                          getCurrencySymbol(
                            pricelist
                              .currencyCode
                          )
                        ),
                        label: `${(
                          getCountryName(
                            pricelist.countryCode
                          )
                        )} - ${(
                          pricelist.label
                        )}`
                      })
                    )
                )
              )
            )
            .toPromise()
      )
    );
  }

  public getCountryCodes(
    domain?: string
  ): Promise<string[]> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/countryCodes`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public getPartnerCodes(
    domain?: string
  ): Promise<string[]> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/partnerCodes`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public getWarehousesCodes(): Promise<any[]> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/warehouses`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public getWarehouseByCode(
    code: string
  ): Promise<any> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/warehouses/${code}`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public getWarehouseByCodePartner(
    codePartner: string
  ): Promise<any> {
    return (
      <any>(
        this.http
            .get(
              `${_WMS_URL}api/warehouses/partners/${codePartner}`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public upsert(
    pricelists: PricelistWms[],
    domain?: string
  ): Promise<PricelistWms[]> {
    return (
      <any>(
        this.http
            .post(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}`,
              pricelists
              .map(
                pricelist => ({
                  ...pricelist
                })
              ),
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public archive(
    _id: string,
    domain?: string
  ): Promise<boolean[]> {
    return (
      <any>(
        this.http
            .post(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/archive`,
              [
                _id
              ],
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public publish(
    _id: string,
    domain?: string
  ): Promise<boolean[]> {
    return (
      <any>(
        this.http
            .post(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/publish`,
              [
                _id
              ],
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public republish(
    _id: string,
    domain?: string
  ): Promise<boolean[]> {
    return (
      <any>(
        this.http
            .post(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/republish`,
              [
                _id
              ],
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public restore(
    _id: string,
    domain?: string
  ): Promise<boolean[]> {
    return (
      <any>(
        this.http
            .post(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/restore`,
              [
                _id
              ],
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public remove(
    _id: string,
    domain?: string
  ): Promise<boolean[]> {
    return (
      <any>(
        this.http
            .delete(
              `${_WMS_URL}api/${(
                getDomainUrlPath(
                  domain
                )
              )}/${_id}`,
              getAuthHeaders(
                this.authToken
              )
            )
            .toPromise()
      )
    );
  }

  public getCloneRatioMapKey(
    isCostLists: boolean = false
  ) {
    return (
      isCostLists ?
        'markupCostsRatioMap' :
        'cloneDiscountRatioMap'
    );
  }

  public resetCloneRatioMap(
    isCostLists: boolean = false
  ) {
    isCostLists ?
      this.resetMarkupCostsRatioMap() :
      this.resetCloneDiscountRatioMap()
  }

  public resetDiscountDefaults() {
    this.discountDefaults = [
      ...PRICELIST_WMS_DISCOUNT_DEFAULTS
    ];
  }

  public resetMarkupRatioMap() {
    this.markupRatioMap = [];
  }

  private resetCloneDiscountRatioMap() {
    this.cloneDiscountRatioMap = [];
  }

  private resetMarkupCostsRatioMap() {
    this.markupCostsRatioMap = [];
  }
}
